import { ApolloClient, ApolloProvider, from, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useAuth0 } from '@auth0/auth0-react';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorView } from '../layout/ErrorView';
import { LoadingView } from '../layout/LoadingView';
import { getConfig } from '../utils/config.ts';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { loginConfig } from '../utils/loginConfig';

export const GraphQlProvider = ({ children }: PropsWithChildren) => {
  const { getAccessTokenSilently, loginWithRedirect, isLoading } = useAuth0();
  const [jwt, setJwt] = useState<string | undefined>();
  const [loadingJwt, setLoadingJwt] = useState(false);
  const [error, setError] = useState(false);
  const { graphQlUrl } = getConfig();
  const client = useMemo(() => {
    const httpLink = new HttpLink({ uri: graphQlUrl });
    const authLink = setContext(async (_, { headers }) => {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${jwt}`,
        },
      };
    });
    const removeTypenameLink = removeTypenameFromVariables();
    const link = from([removeTypenameLink, authLink.concat(httpLink)]);
    return new ApolloClient({ link, cache: new InMemoryCache({ addTypename: false }) });
  }, [graphQlUrl, jwt]);

  const getJwt = useCallback(async () => {
    let token = '';
    try {
      token = await getAccessTokenSilently();
    } catch {
      await loginWithRedirect(loginConfig(window.location.pathname));
      throw new Error('Not logged in. Redirecting...');
    }
    return token;
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (isLoading || loadingJwt) return;
    if (!jwt) {
      setLoadingJwt(true);
      getJwt()
        .then((token) => {
          setJwt(token);
          setLoadingJwt(false);
        })
        .catch(() => setError(true));
    }
  }, [getJwt, isLoading, jwt, loadingJwt]);

  if (!jwt || loadingJwt || isLoading) {
    return <LoadingView />;
  }

  if (error) {
    return <ErrorView message="Could not authenticate user" />;
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
